// Task creation helpers split out for clarity.
import { sql } from "../../../base";
import { getTasksSchemaInfo } from "../../../schemaCache";
import type { Task } from "../../../types";

export const createTaskMutations = {
  async create(data: Omit<Task, 'id' | 'created_at' | 'updated_at'>): Promise<Task> {
        try {
          // Get cached schema information
          const schema = await getTasksSchemaInfo();
          const hasDailyColumns = schema.hasDaily;
          const hasRegularColumns = schema.hasRegular;
          const hasFrequency = schema.hasFrequency;
          const hasTaskType = schema.hasTaskType;
          const hasNextDueDate = schema.hasNextDueDate;
          const hasDependencyColumns = schema.hasDependencyColumns;
          const hasSOPColumn = schema.hasSOPColumn;
        
          // Set defaults
          const taskType = data.task_type || 'To Do';
          const frequency = data.frequency || 'Daily';
        
          // For backward compatibility, set is_regular based on is_daily if provided
          const isRegular = data.is_regular ?? data.is_daily ?? false;
        
          if (hasRegularColumns && hasFrequency && hasTaskType && hasNextDueDate) {
            // Previous schema with scheduling fields
            const result = await sql`
              INSERT INTO tasks (goal_id, user_id, title, description, priority, status, task_type,
                                assigned_to, deadline, start_date, end_date, position, is_regular, frequency, 
                                last_generated_date, next_due_date, recurrence_day)
              VALUES (${data.goal_id}, ${data.user_id}, ${data.title}, ${data.description}, 
                      ${data.priority}, ${data.status}, ${taskType}, ${data.assigned_to}, ${data.deadline}, 
                      COALESCE(${data.start_date}, NOW()), ${data.end_date}, ${data.position}, ${isRegular}, ${frequency}, 
                      ${data.last_generated_date}, ${data.next_due_date}, ${data.recurrence_day})
              RETURNING *
            `;
            const created = result[0] as Task;
            // If dependencies were provided, persist them to task_dependencies and recompute is_live
            if (data.depends_on && data.depends_on.length > 0) {
              const depValues: any[] = [];
              const tuples = data.depends_on.map((depId, idx) => {
                depValues.push(created.id, depId);
                const a = idx * 2 + 1;
                const b = idx * 2 + 2;
                return `($${a}, $${b})`;
              }).join(', ');
              await sqlRaw(`INSERT INTO task_dependencies (task_id, depends_on_task_id) VALUES ${tuples}`, depValues);
              await sqlRaw(
                `UPDATE tasks AS t
                 SET is_live = (
                   CASE WHEN NOT EXISTS (SELECT 1 FROM task_dependencies td WHERE td.task_id = t.id)
                        THEN 1
                        ELSE (
                          CASE WHEN NOT EXISTS (
                                   SELECT 1 FROM task_dependencies td
                                   JOIN tasks d ON d.id = td.depends_on_task_id
                                   WHERE td.task_id = t.id AND (d.status IS NULL OR d.status != 'Complete')
                               ) THEN 1 ELSE 0 END
                        )
                   END
                 )
                 WHERE t.id = $1`,
                [created.id]
              );
            }
            return created;
          } else if (hasRegularColumns && hasFrequency && hasTaskType) {
            // Schema without scheduling fields
            const result = await sql`
              INSERT INTO tasks (goal_id, user_id, title, description, priority, status, task_type,
                                assigned_to, deadline, start_date, end_date, position, is_regular, frequency, last_generated_date)
              VALUES (${data.goal_id}, ${data.user_id}, ${data.title}, ${data.description}, 
                      ${data.priority}, ${data.status}, ${taskType}, ${data.assigned_to}, ${data.deadline}, 
                      COALESCE(${data.start_date}, NOW()), ${data.end_date}, ${data.position}, ${isRegular}, ${frequency}, ${data.last_generated_date})
              RETURNING *
            `;
            const created = result[0] as Task;
            if (data.depends_on && data.depends_on.length > 0) {
              const depValues: any[] = [];
              const tuples = data.depends_on.map((depId, idx) => {
                depValues.push(created.id, depId);
                const a = idx * 2 + 1;
                const b = idx * 2 + 2;
                return `($${a}, $${b})`;
              }).join(', ');
              await sqlRaw(`INSERT INTO task_dependencies (task_id, depends_on_task_id) VALUES ${tuples}`, depValues);
            }
            return created;
          } else if (hasDailyColumns && hasTaskType) {
            // Previous schema with daily tasks and task_type
            const result = await sql`
              INSERT INTO tasks (goal_id, user_id, title, description, priority, status, task_type,
                                assigned_to, deadline, start_date, end_date, position, is_daily, last_generated_date)
              VALUES (${data.goal_id}, ${data.user_id}, ${data.title}, ${data.description}, 
                      ${data.priority}, ${data.status}, ${taskType}, ${data.assigned_to}, ${data.deadline}, 
                      COALESCE(${data.start_date}, NOW()), ${data.end_date}, ${data.position}, ${data.is_daily}, ${data.last_generated_date})
              RETURNING *
            `;
            const created = result[0] as Task;
            if (data.depends_on && data.depends_on.length > 0) {
              const depValues: any[] = [];
              const tuples = data.depends_on.map((depId, idx) => {
                depValues.push(created.id, depId);
                const a = idx * 2 + 1;
                const b = idx * 2 + 2;
                return `($${a}, $${b})`;
              }).join(', ');
              await sqlRaw(`INSERT INTO task_dependencies (task_id, depends_on_task_id) VALUES ${tuples}`, depValues);
            }
            return created;
          } else if (hasDailyColumns) {
            // Fallback for when task_type doesn't exist yet
            const result = await sql`
              INSERT INTO tasks (goal_id, user_id, title, description, priority, status, 
                                assigned_to, deadline, start_date, end_date, position, is_daily, last_generated_date)
              VALUES (${data.goal_id}, ${data.user_id}, ${data.title}, ${data.description}, 
                      ${data.priority}, ${data.status}, ${data.assigned_to}, ${data.deadline}, 
                      COALESCE(${data.start_date}, NOW()), ${data.end_date}, ${data.position}, ${data.is_daily}, ${data.last_generated_date})
              RETURNING *
            `;
            const created = result[0] as Task;
            if (data.depends_on && data.depends_on.length > 0) {
              const depValues: any[] = [];
              const tuples = data.depends_on.map((depId, idx) => {
                depValues.push(created.id, depId);
                const a = idx * 2 + 1;
                const b = idx * 2 + 2;
                return `($${a}, $${b})`;
              }).join(', ');
              await sqlRaw(`INSERT INTO task_dependencies (task_id, depends_on_task_id) VALUES ${tuples}`, depValues);
            }
            return created;
          } else {
            // Fallback for when columns don't exist
            const result = await sql`
              INSERT INTO tasks (goal_id, user_id, title, description, priority, status, 
                                assigned_to, deadline, start_date, end_date, position)
              VALUES (${data.goal_id}, ${data.user_id}, ${data.title}, ${data.description}, 
                      ${data.priority}, ${data.status}, ${data.assigned_to}, ${data.deadline}, 
                      COALESCE(${data.start_date}, NOW()), ${data.end_date}, ${data.position})
              RETURNING *
            `;
            const created = result[0] as Task;
            if (data.depends_on && data.depends_on.length > 0) {
              const depValues: any[] = [];
              const tuples = data.depends_on.map((depId, idx) => {
                depValues.push(created.id, depId);
                const a = idx * 2 + 1;
                const b = idx * 2 + 2;
                return `($${a}, $${b})`;
              }).join(', ');
              await sqlRaw(`INSERT INTO task_dependencies (task_id, depends_on_task_id) VALUES ${tuples}`, depValues);
            }
            return created;
          }
        } catch (error) {
          console.error('Error creating task:', error);
          throw error;
        }
      },
};
